import { GHS_PICTOGRAMS } from "./ghsPictograms";
import { MarkId } from "./layout";

// Simplified, self-contained regulatory/compliance mark graphics — not a
// legal certification tool. In particular, the CE and UKCA marks' exact
// proportions are legally regulated (EU 93/68/EEC and the UK's equivalent);
// verify against the official technical drawings before relying on these
// for real product certification.

interface MarkProps {
  // Overrides the mark's ink color (its default is black). For REACH this
  // only recolors the border/text — the EU flag's blue/yellow stay as-is,
  // since those are the flag's actual colors, not a stylistic choice.
  color?: string;
}

// Traced directly from the public-domain official artwork (Wikimedia
// Commons "Conformité Européenne (logo).svg", by MatthiasDD — two rings
// built from a 100-radius outer arc and a 70-radius inner arc; the second
// ring gets a notch cut for the middle bar to read as "E"). Not hand-drawn.
const CeMark = ({ color = "#000000" }: MarkProps) => (
  <svg viewBox="0 0 280 200" width="100%" height="100%">
    <path
      fill={color}
      d="M110,199.498744A100,100 0 0 1 100,200A100,100 0 0 1 100,0A100,100 0 0 1 110,0.501256L110,30.501256A70,70 0 0 0 100,30A70,70 0 0 0 100,170A70,70 0 0 0 110,169.498744Z"
    />
    <path
      fill={color}
      d="M280,199.498744A100,100 0 0 1 270,200A100,100 0 0 1 270,0A100,100 0 0 1 280,0.501256L280,30.501256A70,70 0 0 0 270,30A70,70 0 0 0 201.620283,85L260,85L260,115L201.620283,115A70,70 0 0 0 270,170A70,70 0 0 0 280,169.498744Z"
    />
  </svg>
);

// Same official geometry as CeMark, just red by default — a separate,
// directly-selectable list entry for stores that print CE in red, rather
// than requiring "add CE, then open the color picker, then pick red".
// Still overridable to any other color via the picker, same as every mark.
const CeRedMark = ({ color }: MarkProps) => <CeMark color={color ?? "#c0392b"} />;

// Traced from the public-domain official artwork (Wikimedia Commons "UKCA
// filled.svg") — the four letterforms as solid shapes, not text glyphs.
const UkcaMark = ({ color = "#000000" }: MarkProps) => (
  <svg viewBox="0 0 640 640" width="100%" height="100%">
    <g fill={color} fillRule="evenodd">
      <path d="M 80 80 V 230 A 70 70 0 0 0 150 300 H 230 A 70 70 0 0 0 300 230 V 80 H 250 V 230 A 20 20 0 0 1 230 250 H 150 A 20 20 0 0 1 130 230 V 80 Z" />
      <path d="M 340 80 V 300 H 390 V 220 L 490 300 H 560 L 420 190 560 80 H 490 L 390 160 V 80 Z" />
      <path d="M 300 340 V 390 H 150 A 20 20 0 0 0 130 410 V 490 A 20 20 0 0 0 150 510 H 300 V 560 H 150 A 70 70 0 0 1 80 490 V 410 A 70 70 0 0 1 150 340 Z" />
      <path d="M 340 560 V 410 A 70 70 0 0 1 410 340 H 490 A 70 70 0 0 1 560 410 V 560 H 510 V 510 H 390 V 560 Z M 390 460 H 510 V 410 A 20 20 0 0 0 490 390 H 410 A 20 20 0 0 0 390 410 Z" />
    </g>
  </svg>
);

// Traced from the official IEC 60417-6414 pictogram (Wikimedia Commons
// "IEC 60417 - Ref-No 6414.svg", CC0) — the exact symbol Directive
// 2012/19/EU specifies for WEEE marking. Corner registration brackets from
// the source file are omitted (a print-alignment guide, not part of the
// pictogram).
const WeeeMark = ({ color = "#000000" }: MarkProps) => (
  <svg viewBox="0 0 200 200" width="100%" height="100%">
    <g fill="none" stroke={color} strokeWidth={5}>
      <circle cx={133} cy={160.5} r={12} strokeWidth={6} />
      <path d="m94,22h23v9H94Zm48,35h12.5V71H142ZM52,40h90" />
      <path d="m 134,150 9.5,-110 c0,-3-1.5,-5-3.5,-7-5,-5-15,-7.5-23,-7.5M93,25c-13,0-24,3-29,8-2,2-3.5,4-3.5,7l11,123c0,1 0.5,1.5 1.5,1.5h50" />
      <path d="m21.5,178.5 157,-157zm0,-157 157,157z" />
    </g>
    <path fill={color} d="m 69,23h9v17h-9zm23,45h29V78.5H92Zm-20,97h10v10H72Z" />
    <circle cx={133} cy={160.5} r={5} fill={color} />
    <circle cx={149} cy={36} r={7} fill={color} />
  </svg>
);

const RohsMark = ({ color = "#000000" }: MarkProps) => (
  <svg viewBox="0 0 100 100" width="100%" height="100%">
    <path
      d="M 20 42 L 40 62 L 82 15"
      fill="none"
      stroke={color}
      strokeWidth={10}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <text
      x={50}
      y={90}
      fontSize={26}
      fontWeight={800}
      fill={color}
      textAnchor="middle"
      fontFamily="Arial, sans-serif"
    >
      RoHS
    </text>
  </svg>
);

// Traced from the public-domain official artwork (Wikimedia Commons
// "Recycling symbol.svg", by Krdan) — the universal Möbius-loop recycling
// symbol as a single filled path, not hand-built from rotated strokes.
const RECYCLE_PATH =
  "m 69.354446,101.88511 14.89909,-26.728603 0.07083,9.77531 31.356544,-0.0708 c 5.60294,-0.59389 10.51526,-2.87142 14.07266,-7.15441 -11.35361,23.224743 -14.62376,39.657983 -38.180359,39.667893 h -7.248845 l -0.07083,10.86145 z m -35.889999,15.7255 C 16.700024,112.51251 8.878305,91.859047 1.210673,77.305177 c 3.514375,3.19524 10.091148,7.76897 13.911042,7.76935 7.326307,0.0171 14.652641,0.0308 21.978959,0.0462 h 22.195134 l -0.07084,32.395433 -0.07083,0.0945 z M 0,68.842237 10.71446,46.989097 0.266195,41.249927 h 31.309304 l 15.135199,27.48417 -10.264323,-6.14596 c -3.292197,6.95205 -6.584391,13.90409 -9.876589,20.85613 -3.788885,-0.0663 -7.57777,-0.13252 -11.366656,-0.19879 C 7.815082,81.522977 2.675268,75.708847 0,68.842237 Z M 104.63055,83.113707 90.794007,57.612917 118.75045,40.683247 132.28,67.789607 c 0.40966,6.48698 -10.06394,15.51407 -16.56466,15.44384 z M 22.791894,23.446554 37.075173,2.040204 C 50.928643,-3.11938 58.95345,8.0193796 64.018171,15.040736 L 50.55943,40.872087 Z M 76.91024,34.969127 C 74.037439,28.196447 70.199044,20.449415 65.399134,13.713557 61.101835,7.6830406 55.909596,2.421532 51.976134,0.07082 L 88.102273,0 c 6.487814,0.582446 9.640194,4.364747 13.080937,9.5391756 l 5.19458,9.1377864 8.02805,-5.123763 -15.111563,26.917529 -32.584352,0.0708 z";

const RecycleMark = ({ color = "#000000" }: MarkProps) => (
  <svg viewBox="0 0 132.29201 128.23599" width="100%" height="100%">
    <path fill={color} d={RECYCLE_PATH} />
  </svg>
);

const STAR_POINTS =
  "0,-10 2.351,-3.236 9.511,-3.090 3.804,1.236 5.878,8.090 0,4 -5.878,8.090 -3.804,1.236 -9.511,-3.090 -2.351,-3.236";
// The EU flag's 12 stars, evenly spaced around a circle, each upright.
const STAR_ANGLES_DEG = Array.from({ length: 12 }, (_, i) => -90 + i * 30);
const REACH_STAR_CENTER = { cx: 50, cy: 64, r: 24 };

const ReachMark = ({ color = "#1a3a8f" }: MarkProps) => (
  <svg viewBox="0 0 100 130" width="100%" height="100%">
    <rect x={4} y={4} width={92} height={122} fill="#ffffff" stroke={color} strokeWidth={3} />
    <text
      x={50}
      y={24}
      fontSize={19}
      fontWeight={800}
      fill={color}
      textAnchor="middle"
      fontFamily="Arial, sans-serif"
    >
      REACH
    </text>
    <rect x={14} y={30} width={72} height={68} fill={color} />
    {STAR_ANGLES_DEG.map(angleDeg => {
      const angleRad = (angleDeg * Math.PI) / 180;
      const x = REACH_STAR_CENTER.cx + REACH_STAR_CENTER.r * Math.cos(angleRad);
      const y = REACH_STAR_CENTER.cy + REACH_STAR_CENTER.r * Math.sin(angleRad);

      return (
        <polygon
          key={angleDeg}
          points={STAR_POINTS}
          fill="#ffcc00"
          transform={`translate(${x} ${y}) scale(0.6)`}
        />
      );
    })}
    <text
      x={50}
      y={116}
      fontSize={11.5}
      fontWeight={800}
      fill={color}
      textAnchor="middle"
      fontFamily="Arial, sans-serif"
      letterSpacing={0.3}
    >
      COMPLIANCE
    </text>
  </svg>
);

// Traced from the live storefront's header logo (maxcei.pt), which draws it
// as a two-tone SVG: a red "M" swoosh (`ICON_PATH`) plus a dark-grey
// wordmark split across several path segments (`WORDMARK_PATHS`). Update
// these paths if the site's own logo artwork ever changes.
const BRAND_ICON_PATH =
  "m 43.641004,51.330767 c -11.12002,-2.39244 -27.624053,-8.29847 -30.183033,-19.28871 -0.55124,-2.36641 -0.009,-4.57423 0.87254,-6.54864 2.32776,-5.2155 7.554,-8.69893 13.06008,-11.29896 0.24496,-0.11551 0.52856,-0.26084 0.6895,-0.37196 l 4.899373,-1.96861 c 2.19917,-0.81996 7.26895,-2.3635497 9.72983,-2.8562797 0.65237,-0.13056 1.14401,-0.32723 1.59614,-0.42669 l 8.95849,-1.48074 c 14.89503,-1.89491 31.76827,-1.21589 45.9249,2.50169 7.493826,1.9678097 17.049126,5.6350697 22.188326,10.7118897 4.59699,4.5412 6.80577,10.83432 2.24959,16.92683 -3.22695,4.31511 -8.95747,7.74855 -14.52894,9.93693 -2.65086,1.0412 -9.733086,3.71219 -12.704606,3.8843 0.009,-6.89364 0.17972,-14.07016 -0.0633,-20.93344 -0.0969,-2.7357 -0.32846,-6.77226 -1.47269,-9.02729 -1.33406,-2.62945 -3.66997,-3.62364 -7.804,-3.18224 -6.08174,0.64959 -9.2855,5.5101 -11.0467,10.58919 -0.87659,2.52792 -4.38137,13.97921 -5.59537,15.5441 -0.74455,-0.70472 -6.46099,-17.53642 -10.0255,-22.32695 -1.5075,-2.02574 -3.55144,-3.64136 -7.48378,-3.73157 -7.87133,-0.18042 -9.56679,5.5635 -9.54815,12.3839 0.0186,6.87139 0.42915,14.06582 0.28728,20.96325 z m 9.16668,13.64473 0.009,-33.55714 c 0.0886,0.5193 2.67384,5.83354 3.15031,6.83565 1.11021,2.33598 2.1926,4.68901 3.29587,7.01181 1.97314,4.15466 4.56304,10.90715 9.93397,10.96281 5.6796,0.0589 8.66326,-6.8204 10.41601,-10.34744 1.18171,-2.37826 2.19302,-4.54986 3.4118,-6.93871 0.396,-0.77602 2.95043,-6.14098 3.70602,-6.68565 l 0.0438,32.68851 c 2.2735,0.14547 7.55566,-1.26409 9.8803,-1.76249 9.363956,-2.00816 18.270366,-4.62669 26.316006,-9.1396 7.86894,-4.41405 20.91761,-14.40664 14.93888,-27.8778 -4.37465,-9.85697 -16.56305,-15.92831 -26.78532,-19.7482297 -22.987986,-8.59041 -59.424106,-8.49194 -82.571739,-0.0907 -10.06276,3.65214 -22.6165386,9.8991797 -26.9585336,19.5873797 -2.846625,6.35183 -1.65428898,12.48642 1.785544,17.65551 7.2394746,10.87926 25.8235596,16.69695 39.1703426,19.49424 3.1597,0.66218 7.05657,1.57375 10.25767,1.91189 z";

const BRAND_WORDMARK_PATHS = [
  "m 451.59917,59.276447 0.002,-11.8695 -44.1544,0.0499 -0.0885,-5.1602 44.1374,-0.0561 0.0757,-7.8755 -44.1927,0.008 0.0278,-4.8124 44.1499,-0.0567 0.0714,-11.7989 c -12.8001,0.3226 -25.9146,0.0842 -38.713,0.0437 -6.3581,-0.0199 -10.3776,-0.5789 -14.9511,4.2338 -3.7332,3.9282 -2.9726,10.2311 -2.9692,16.3885 0.003,6.0743 -0.7728,12.3888 2.7952,16.3219 4.263,4.6995 7.1416,4.5538 14.8853,4.5507 12.8531,-0.005 26.1213,-0.3464 38.9239,0.0329 z",
  "m 197.15547,46.068647 c -1.7079,-0.6542 -2.3233,-3.9183 -3.0166,-5.6313 l -5.4273,-12.0886 c -1.1338,-2.2827 -2.0347,-3.8432 -3.2785,-5.6081 -5.2798,-7.4919 -13.5868,-4.6527 -16.3077,1.0602 -1.302,2.7337 -1.2705,4.3747 -1.2702,7.6102 l 0.0635,28.4485 10.1185,-0.0655 c 0.4932,-2.7893 0.3762,-20.3003 0.2259,-25.0189 1.0384,0.72 7.0711,11.3902 8.0966,13.4243 2.3043,4.5712 5.2302,10.7278 10.0897,11.5609 3.7698,0.6466 5.7835,-1.7751 7.1569,-3.6678 0.5303,-0.731 1.3949,-2.5007 1.8465,-3.4276 l 5.33,-10.9999 c 0.7194,-1.4836 2.6376,-6.6739 3.7068,-7.2737 l 0.038,25.2394 12.4152,0.1395 0.0652,-28.3592 c 0.005,-6.2019 -1.5134,-10.1183 -5.4981,-12.3676 -4.9802,-2.8108 -10.5007,-0.0108 -12.8251,2.7763 -4.028,4.8294 -8.3763,22.2534 -11.5293,24.2489 z",
  "m 391.96987,59.188547 0.0933,-11.7612 c -5.6177,-0.2821 -42.5855,0.7115 -44.1905,-0.2152 -0.3557,-1.0893 -0.2415,-15.4607 -0.0932,-17.5827 l 44.2156,-0.1052 0.057,-11.819 c -12.7046,-0.1894 -25.9242,-0.0698 -38.6569,0.0643 -6.4176,0.0678 -10.3037,-0.8027 -15.0355,4.1261 -3.6876,3.8413 -3.0113,10.1974 -3.0144,16.4753 -0.003,6.7249 -0.887,11.6807 2.8678,16.3797 3.5734,4.4725 7.6351,4.4342 14.9423,4.4804 6.4763,0.0411 12.962,0.002 19.4397,0.0108 3.6777,0.005 16.8106,0.1928 19.3748,-0.0533 z",
  "m 308.51537,30.905347 c -1.2456,-1.1373 -2.6793,-2.4962 -4.0553,-3.602 -0.7406,-0.5956 -1.2353,-1.2237 -2.0026,-1.8363 -0.8229,-0.6573 -1.3162,-1.317 -2.1212,-1.9584 -0.9023,-0.7192 -1.1168,-0.9508 -2.0092,-1.8292 -0.7489,-0.7373 -1.4343,-1.1237 -2.1711,-1.9117 -2.2714,-2.4285 -2.3221,-1.9197 -6.8408,-1.9114 l -8.2001,0.0215 12.9425,12.3919 c 0.9751,0.8419 1.6055,1.6441 2.5356,2.5033 1.7963,1.6594 3.4568,2.9019 5.0904,4.8779 -0.578,0.9553 -9.7498,9.496 -11.2861,11.16 l -34.3965,33.1619 10.0344,0.0341 c 3.8602,-0.005 3.3556,0.4838 6.8595,-2.9365 l 15.5625,-14.6755 c 0.9295,-0.9332 1.7887,-1.3958 2.7117,-2.3309 l 2.5545,-2.4846 c 0.9049,-0.8025 1.6617,-1.722 2.5631,-2.4746 1.0352,-0.8646 1.7158,-1.3726 2.6314,-2.4109 0.8612,-0.9768 1.6903,-1.5593 2.6433,-2.3984 0.9397,-0.8274 1.6183,-1.6852 2.5572,-2.4795 1.2719,-1.0757 4.21,-4.2148 5.3566,-4.7868 0.6834,0.3841 2.1189,1.9216 2.8199,2.5234 l 11.0976,10.5055 c 1.0506,1.0737 2.8786,0.7168 4.8025,0.731 3.6913,0.0267 7.4171,-0.0694 11.0993,0.0241 -0.4904,-0.7259 -3.1286,-2.8738 -4.1238,-3.899 l -7.1932,-6.4885 c -1.7334,-1.7507 -10.5758,-9.0947 -11.2363,-10.2946 l 20.6572,-19.3556 c 1.0687,-0.9209 1.0075,-0.6304 1.4352,-1.6336 l -12.3182,-0.0394 c -4.557,-2e-4 -3.2417,0.169 -5.7232,2.344 -0.7653,0.671 -1.237,1.1908 -1.9819,1.8592 l -1.9937,2.0849 c -1.2826,1.1974 -7.4001,7.1535 -8.3012,7.5147 z",
  "m 250.57777,36.081347 c 0.2713,-0.8484 3.1303,-6.8074 4.0192,-7.3111 l 3.3673,7.3273 z m -23.2648,23.1597 10.9159,0.5139 5.4817,-10.5647 20.6413,-0.0335 4.4804,9.4646 10.9676,-0.0258 c -0.328,-1.4825 -1.9027,-4.3217 -2.6391,-5.9125 l -8.1499,-17.5164 c -2.728,-6.0868 -8.1451,-20.107 -15.6367,-18.5788 -6.4154,1.3087 -11.4429,13.1105 -14.679,19.6543 -1.8581,3.7573 -3.8239,7.4534 -5.7413,11.3065 -1.1015,2.2136 -5.2055,9.9573 -5.6409,11.6924 z",
  "m 471.54607,59.131047 -0.0686,-41.6605 c -2.6672,-0.1009 -5.4709,-0.0871 -8.1675,-0.0641 -2.9279,0.025 -4.1919,1.2524 -4.195,4.1604 l -0.0113,33.1254 c -0.005,3.005 0.93,4.4116 3.9639,4.4501 2.7403,0.0346 5.7555,0.0828 8.4785,-0.0113 z",
];

// Unlike the compliance marks, an explicit color override recolors the
// whole wordmark one flat color (useful for single-color printing) — the
// default keeps the two-tone brand red/grey.
const BrandMark = ({ color }: MarkProps) => (
  <svg
    viewBox="0 0 471.54608 82.028458"
    width="100%"
    height="100%"
    preserveAspectRatio="xMidYMid meet"
  >
    <path d={BRAND_ICON_PATH} fillRule="evenodd" fill={color ?? "#c0392b"} />
    {BRAND_WORDMARK_PATHS.map((d, index) => (
      <path key={index} d={d} fillRule="evenodd" fill={color ?? "#404040"} />
    ))}
  </svg>
);

// BRAND_WORDMARK_PATHS' ink doesn't start at x=0 in its declared viewBox —
// it sits at x≈167.9-471.5, since these paths were traced from the
// storefront's horizontal lockup where that left span is the icon's slot.
// BrandMark (inline) uses the raw viewBox because the icon fills that slot.
// Here there's no icon beside it, so the wordmark sub-svg must crop to the
// text's own ink bounds (measured via getBBox) instead of the raw viewBox,
// or the text renders shifted right with dead space on its left.
const StackedBrandMark = ({ color }: MarkProps) => (
  <svg viewBox="0 0 255 121" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
    <svg x="62.5" y="0" width="130" height="60.07" viewBox="0 0 145 67">
      <path d={BRAND_ICON_PATH} fillRule="evenodd" fill={color ?? "#c0392b"} />
    </svg>
    <svg x="0" y="66.07" width="255" height="55.06" viewBox="167.86 16.47 303.69 65.56">
      {BRAND_WORDMARK_PATHS.map((d, index) => (
        <path key={index} d={d} fillRule="evenodd" fill={color ?? "#404040"} />
      ))}
    </svg>
  </svg>
);

// GHS hazard pictograms keep their official red border and black symbol —
// CLP fixes those colors, so the color override is ignored.
const ghsMark = (id: keyof typeof GHS_PICTOGRAMS) => {
  const GhsMark = () => (
    <svg
      viewBox={GHS_PICTOGRAMS[id].viewBox}
      width="100%"
      height="100%"
      dangerouslySetInnerHTML={{ __html: GHS_PICTOGRAMS[id].markup }}
    />
  );

  return GhsMark;
};

const MARK_COMPONENTS: Record<MarkId, (props: MarkProps) => JSX.Element> = {
  ce: CeMark,
  "ce-red": CeRedMark,
  ukca: UkcaMark,
  weee: WeeeMark,
  rohs: RohsMark,
  recycle: RecycleMark,
  reach: ReachMark,
  ghs01: ghsMark("ghs01"),
  ghs02: ghsMark("ghs02"),
  ghs03: ghsMark("ghs03"),
  ghs04: ghsMark("ghs04"),
  ghs05: ghsMark("ghs05"),
  ghs06: ghsMark("ghs06"),
  ghs07: ghsMark("ghs07"),
  ghs08: ghsMark("ghs08"),
  ghs09: ghsMark("ghs09"),
  brand: BrandMark,
  "brand-stacked": StackedBrandMark,
};

export const MarkGraphic = ({ markId, color }: { markId: MarkId; color?: string }) => {
  const Graphic = MARK_COMPONENTS[markId];

  return <Graphic color={color} />;
};
